import React, { useCallback, useEffect, useMemo, useState } from 'react';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNavigate } from 'react-router-dom';
import { CrmBadge, CrmButton, CrmInput, CrmSelect } from '../components/ui';
import { useNotification } from '../context/NotificationContext';
import { fetchNotificationsApi } from '../services/notificationService';
import { formatDateTime } from '../utils/dateFormatter';
import '../assets/styles/Phase13Settings.scss';

const moduleLabel = (value) => String(value || 'General')
  .replace(/_/g, ' ')
  .replace(/\b\w/g, (char) => char.toUpperCase())
  .replace(/^Leads$/i, 'Contacts')
  .replace(/^Kot$/i, 'KOT');

const fallbackRoute = (notification) => {
  const moduleName = String(notification?.module || '').toLowerCase();
  const sourceId = Number(notification?.source_id || 0);
  if (moduleName === 'leads' && sourceId) return `/leads/${sourceId}/edit`;
  if (moduleName === 'orders' && sourceId) return `/orders/${sourceId}`;
  if (moduleName === 'work_orders' && sourceId) return `/workorders/${sourceId}`;
  if (moduleName === 'invoices' && sourceId) return `/invoices/${sourceId}`;
  if (moduleName === 'quotations' && sourceId) return `/quotations/${sourceId}`;
  if (moduleName === 'kot') return '/kots';
  if (moduleName === 'delivery') return '/deliveries';
  if (moduleName === 'feedback') return '/feedbacks';
  return '/dashboard';
};

const errorText = (error) => error?.response?.data?.error || error?.message || 'Failed to load notifications.';

export default function NotificationCenter() {
  const navigate = useNavigate();
  const { markNotificationSeen, markAllNotificationsSeen, fetchBubbleCounts, fetchNotifications } = useNotification();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ count: 0, filteredCount: 0, unSeenNotifications: 0, seenNotifications: 0 });
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ search: '', module: '', unreadOnly: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const perPage = 30;

  const load = useCallback(async (nextPage = 1) => {
    setLoading(true);
    setError('');
    try {
      const payload = await fetchNotificationsApi({
        page: nextPage,
        per_page: perPage,
        search: filters.search || undefined,
        module: filters.module || undefined,
        unread_only: filters.unreadOnly ? 1 : undefined,
      });
      setItems(Array.isArray(payload?.result) ? payload.result : []);
      setSummary({
        count: Number(payload?.count || 0),
        filteredCount: Number(payload?.filteredCount || 0),
        unSeenNotifications: Number(payload?.unSeenNotifications || 0),
        seenNotifications: Number(payload?.seenNotifications || 0),
      });
      setPage(Number(payload?.page || nextPage));
    } catch (loadError) {
      setError(errorText(loadError));
    } finally {
      setLoading(false);
    }
  }, [filters.module, filters.search, filters.unreadOnly]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setPage(1);
      load(1);
    }, filters.search ? 250 : 0);
    return () => window.clearTimeout(timer);
  }, [filters.search, filters.module, filters.unreadOnly, load]);

  const moduleOptions = useMemo(() => {
    const values = new Set(items.map((item) => String(item.module || '').trim()).filter(Boolean));
    return [...values].sort();
  }, [items]);

  const totalPages = Math.max(1, Math.ceil(Number(summary.filteredCount || 0) / perPage));

  const openNotification = async (notification) => {
    if (!notification?.isSeen) {
      await markNotificationSeen(notification.id);
      setItems((rows) => rows.map((row) => Number(row.id) === Number(notification.id) ? { ...row, isSeen: true } : row));
      setSummary((current) => ({
        ...current,
        unSeenNotifications: Math.max(0, Number(current.unSeenNotifications || 0) - 1),
        seenNotifications: Number(current.seenNotifications || 0) + 1,
      }));
    }
    const route = notification.redirect_url || fallbackRoute(notification);
    navigate(route);
  };

  const markEverythingRead = async () => {
    await markAllNotificationsSeen();
    await Promise.all([fetchBubbleCounts(), fetchNotifications()]);
    await load(page);
  };

  return (
    <div className="phase13-shell notification-center">
      <div className="phase13-section-heading">
        <div>
          <h2>Notification Center</h2>
          <p>All dashboard notifications for your account, including operational alerts created by the communication engine.</p>
        </div>
        <div className="phase13-inline-actions">
          <CrmButton variant="secondary" startIcon={<RefreshIcon />} onClick={() => load(page)} loading={loading}>Refresh</CrmButton>
          <CrmButton startIcon={<MarkEmailReadOutlinedIcon />} onClick={markEverythingRead} disabled={!summary.unSeenNotifications}>Mark all as read</CrmButton>
        </div>
      </div>

      {error ? <div className="phase13-error" role="alert">{error}</div> : null}

      <div className="phase13-summary-grid">
        <div className="phase13-summary-card"><span>Total</span><strong>{summary.count}</strong></div>
        <div className="phase13-summary-card"><span>Unread</span><strong>{summary.unSeenNotifications}</strong></div>
        <div className="phase13-summary-card"><span>Read</span><strong>{summary.seenNotifications}</strong></div>
      </div>

      <div className="phase13-toolbar">
        <div className="phase13-toolbar__filters notification-center__filters">
          <CrmInput label="Search notifications" value={filters.search} onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value }))} placeholder="Order, payment, quotation…" />
          <CrmSelect label="Module" value={filters.module} onChange={(event) => setFilters((current) => ({ ...current, module: event.target.value }))}>
            <option value="">All modules</option>
            {moduleOptions.map((moduleName) => <option key={moduleName} value={moduleName}>{moduleLabel(moduleName)}</option>)}
          </CrmSelect>
          <CrmSelect label="Status" value={filters.unreadOnly ? 'unread' : 'all'} onChange={(event) => setFilters((current) => ({ ...current, unreadOnly: event.target.value === 'unread' }))}>
            <option value="all">All</option>
            <option value="unread">Unread only</option>
          </CrmSelect>
        </div>
      </div>

      <div className="notification-center__list" aria-busy={loading}>
        {!loading && !items.length ? <div className="phase13-empty">No notifications match these filters.</div> : null}
        {items.map((notification) => (
          <button key={notification.id} type="button" className={`notification-center__item ${notification.isSeen ? 'is-read' : 'is-unread'}`} onClick={() => openNotification(notification)}>
            <span className="notification-center__dot" aria-hidden="true" />
            <span className="notification-center__body">
              <span className="notification-center__title">{notification.action || 'Dashboard update'}</span>
              <span className="notification-center__meta">
                <CrmBadge tone={notification.isSeen ? 'neutral' : 'info'}>{notification.isSeen ? 'Read' : 'Unread'}</CrmBadge>
                <span>{moduleLabel(notification.module)}</span>
                <span>{formatDateTime(notification.created_at || new Date())}</span>
                {notification.byUser?.name ? <span>By {notification.byUser.name}</span> : null}
              </span>
            </span>
            <OpenInNewIcon fontSize="small" />
          </button>
        ))}
      </div>

      <div className="notification-center__pagination">
        <CrmButton variant="secondary" size="small" disabled={page <= 1 || loading} onClick={() => load(page - 1)}>Previous</CrmButton>
        <span>Page {page} of {totalPages}</span>
        <CrmButton variant="secondary" size="small" disabled={page >= totalPages || loading} onClick={() => load(page + 1)}>Next</CrmButton>
      </div>
    </div>
  );
}
